import React, { startTransition, useEffect, useState } from 'react';

import DashboardHero from './components/DashboardHero';
import PreviewTable from './components/PreviewTable';
import ReportDetailView from './components/ReportDetailView';
import ReportsPanel from './components/ReportsPanel';
import UploadForm from './components/UploadForm';
import { DEFAULT_FORM_VALUES } from './constants/defaultFormValues';
import { fetchReports, getReportDownloadUrl, uploadReport } from './services/reportApi';
import './styles/app.css';
import { downloadReport } from './utils/download';

import { Toaster, toast } from 'react-hot-toast';
import ExcelManagementView from './ExcelManagementView';
import ExcelComparisonView from './ExcelComparisonView';

export default function App() {
  const [form, setForm] = useState(DEFAULT_FORM_VALUES);
  const [preview, setPreview] = useState([]);
  const [latestReport, setLatestReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedReport, setSelectedReport] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [totalReports, setTotalReports] = useState(0);

  useEffect(() => {
    void loadReports(1, '');
  }, []);

  async function loadReports(targetPage = page, targetSearch = search) {
    setReportsLoading(true);

    try {
      const payload = await fetchReports(targetPage, 10, targetSearch);
      const firstReport = payload.data && payload.data[0] ? payload.data[0] : null;

      startTransition(() => {
        setReports(payload.data || []);
        setTotalRows(payload.totalRows || 0);
        setTotalReports(payload.total || 0);
        setPage(payload.page || 1);
        if (targetPage === 1 && !targetSearch) {
          setLatestReport((currentLatest) => currentLatest || firstReport);
          setPreview((currentPreview) => (currentPreview.length > 0 ? currentPreview : (firstReport && firstReport.preview ? firstReport.preview : [])));
        }
      });
    } catch (error) {
      toast.error(error.message || 'Could not load saved reports.');
    } finally {
      setReportsLoading(false);
    }
  }

  function updateField(field, value) {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value
    }));
  }

  function updateFile(field, value) {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!form.file) {
      toast.error('Choose an Excel file before generating the report.');
      return;
    }

    setSubmitting(true);
    toast('Generating Excel workbook. This can take a moment for large files.', { icon: '⏳' });

    const formData = new FormData();
    formData.append('file', form.file);

    if (form.media) {
      formData.append('media', form.media);
    }

    formData.append('message', form.message.trim());
    formData.append('content', form.content.trim());
    formData.append('pending', String(form.pending));
    formData.append('sent', String(form.sent));
    formData.append('delivered', String(form.delivered));
    formData.append('read', String(form.read));
    formData.append('failed', String(form.failed));
    formData.append('rate', String(form.rate || 1000));

    if (form.campaignDatetime) {
      formData.append('campaignDatetime', form.campaignDatetime);
    }

    try {
      const payload = await uploadReport(formData);

      startTransition(() => {
        if (payload.meta) {
          setLatestReport(payload.meta);
          setReports((currentReports) => [
            payload.meta,
            ...currentReports.filter((report) => report.id !== payload.meta.id)
          ]);
        }

        setPreview(payload.preview || []);
      });

      toast.success('Workbook created. Click Download Excel to save the .xlsx file.');
    } catch (error) {
      toast.error(error.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDownload(report) {
    setDownloadingId(report.id);

    try {
      await downloadReport(getReportDownloadUrl(report.id), report.filename || 'report.xlsx');
      toast.success(`${report.filename || 'Report'} downloaded successfully.`);
    } catch (error) {
      toast.error(error.message || 'Download failed.');
    } finally {
      setDownloadingId('');
    }
  }

  function handleDeleteSuccess(deletedId) {
    if (latestReport && latestReport.id === deletedId) {
      setLatestReport(null);
      setPreview([]);
    }
  }

  function handleViewDetail(report) {
    setSelectedReport(report);
    setActiveTab('report-detail');
  }

  function handleBackToReports() {
    setSelectedReport(null);
    setActiveTab('reports');
  }

  return (
    <div className="admin-layout">
      <Toaster position="top-right" />
      <div className="page-backdrop" />
      
      <aside className="admin-sidebar">
        <div className="admin-sidebar__logo">
          <h2>Admin Panel</h2>
        </div>
        <nav className="admin-sidebar__nav">
          <button 
            className={`admin-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`admin-nav-item ${activeTab === 'reports' || activeTab === 'report-detail' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            Report Generator
          </button>
          
          <div style={{ marginTop: '24px', padding: '0 16px', fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Excel Work
          </div>
          <button 
            className={`admin-nav-item ${activeTab === 'excel-management' ? 'active' : ''}`}
            onClick={() => setActiveTab('excel-management')}
          >
            Excel Management
          </button>
          <button 
            className={`admin-nav-item ${activeTab === 'excel-comparison' ? 'active' : ''}`}
            onClick={() => setActiveTab('excel-comparison')}
          >
            Excel Comparison
          </button>
        </nav>
      </aside>

      <main className="admin-main">
        {activeTab === 'dashboard' && (
          <div className="dashboard-view">
            <DashboardHero
              latestReport={latestReport || (reports[0] || null)}
              reportCount={totalReports}
              submitting={submitting}
              totalRows={totalRows}
            />

            <div className="main-stack">
              <UploadForm
                form={form}
                onFieldChange={updateField}
                onFileChange={updateFile}
                onRefresh={loadReports}
                onSubmit={handleSubmit}
                refreshing={reportsLoading}
                submitting={submitting}
              />
              <PreviewTable
                downloading={Boolean(latestReport) && downloadingId === latestReport.id}
                onDownload={handleDownload}
                preview={preview}
                report={latestReport}
              />
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="reports-view">
            <ReportsPanel
              downloadingId={downloadingId}
              loading={reportsLoading}
              onDownload={handleDownload}
              reports={reports}
              page={page}
              search={search}
              total={totalReports}
              onPageChange={(p) => {
                setPage(p);
                loadReports(p, search);
              }}
              onSearchChange={(s) => {
                setSearch(s);
                setPage(1);
                loadReports(1, s);
              }}
              onRefresh={() => loadReports(page, search)}
              onDeleteSuccess={handleDeleteSuccess}
              onViewDetail={handleViewDetail}
            />
          </div>
        )}

        {activeTab === 'report-detail' && selectedReport && (
          <div className="report-detail-wrapper">
            <ReportDetailView 
              report={selectedReport} 
              onBack={handleBackToReports} 
            />
          </div>
        )}
        
        {activeTab === 'excel-management' && (
          <div className="dashboard-view">
            <ExcelManagementView />
          </div>
        )}

        {activeTab === 'excel-comparison' && (
          <div className="dashboard-view">
            <ExcelComparisonView />
          </div>
        )}
      </main>
    </div>
  );
}
